import React from 'react';
import { clsx } from 'clsx';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import type { Urgency, TokenStatus } from './types';

// ── MetricCard ──────────────────────────────────────────────────────────────
export interface MetricCardProps {
  icon:    React.ElementType;
  label:   string;
  value:   string | number;
  sub?:    React.ReactNode;
  accent?: string;
  alert?:  boolean;
}

export function MetricCard({ icon: Icon, label, value, sub, accent = 'bg-orange/15', alert }: MetricCardProps) {
  return (
    <div className={clsx(
      'bg-white rounded-2xl border shadow-card p-5 flex flex-col gap-3',
      alert ? 'border-red-200' : 'border-gray-100',
    )}>
      <div className="flex items-center justify-between">
        <div className={clsx('w-9 h-9 rounded-xl flex items-center justify-center', accent)}>
          <Icon size={17} className="text-navy"/>
        </div>
        {alert && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"/>}
      </div>
      <div>
        <div className={clsx('text-2xl font-bold leading-tight', alert ? 'text-red-600' : 'text-navy')}>{value}</div>
        <div className="text-xs text-gray-400 mt-0.5">{label}</div>
      </div>
      {sub && <div className="text-xs text-gray-500 border-t border-gray-100 pt-2">{sub}</div>}
    </div>
  );
}

// ── SectionCard ─────────────────────────────────────────────────────────────
export function SectionCard({ title, action, children, className }: {
  title:     string;
  action?:   React.ReactNode;
  children:  React.ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden', className)}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-navy">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

// ── UrgencyBadge ────────────────────────────────────────────────────────────
export function UrgencyBadge({ urgency }: { urgency: Urgency }) {
  const map: Record<Urgency, string> = {
    STAT:    'bg-red-100 text-red-700 border-red-200',
    URGENT:  'bg-orange/15 text-orange-dark border-orange/30',
    ROUTINE: 'bg-gray-100 text-gray-500 border-gray-200',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold border', map[urgency])}>
      {urgency}
    </span>
  );
}

// ── TokenStatusBadge ────────────────────────────────────────────────────────
export function TokenStatusBadge({ status }: { status: TokenStatus }) {
  const map: Record<TokenStatus, string> = {
    waiting:     'bg-amber-50 text-amber-700 border-amber-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    completed:   'bg-emerald-50 text-emerald-700 border-emerald-200',
    skipped:     'bg-gray-100 text-gray-500 border-gray-200',
  };
  const label: Record<TokenStatus, string> = {
    waiting: 'Waiting', in_progress: 'In Progress', completed: 'Done', skipped: 'Skipped',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold border', map[status])}>
      {label[status]}
    </span>
  );
}

// ── GenericBadge ────────────────────────────────────────────────────────────
export function GenericBadge({ label, variant = 'neutral' }: {
  label:   string;
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'amber';
}) {
  const map = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border-amber-200',
    danger:  'bg-red-50 text-red-700 border-red-200',
    info:    'bg-blue-50 text-blue-700 border-blue-200',
    neutral: 'bg-gray-100 text-gray-500 border-gray-200',
    amber:   'bg-orange/15 text-orange-dark border-orange/30',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-semibold border', map[variant])}>
      {label}
    </span>
  );
}

// ── TATBar ──────────────────────────────────────────────────────────────────
export function TATBar({ elapsed, tat }: { elapsed: number; tat: number }) {
  const pct = Math.min(100, Math.round((elapsed / tat) * 100));
  const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }}/>
      </div>
      <span className="text-2xs font-mono" style={{ color }}>{elapsed}m</span>
    </div>
  );
}

// ── DashboardShell ──────────────────────────────────────────────────────────
export function DashboardShell({ title, subtitle, onRefresh, refreshing, children }: {
  title:      string;
  subtitle?:  string;
  onRefresh?: () => void;
  refreshing?: boolean;
  children:   React.ReactNode;
}) {
  return (
    <div className="min-h-full bg-[#f4f6fb] px-4 sm:px-6 py-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-navy">{title}</h1>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-navy
                       border border-gray-200 bg-white rounded-lg px-3 py-1.5 shadow-sm transition shrink-0">
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''}/>
            Refresh
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

// ── Loading / Error states ──────────────────────────────────────────────────
export function LoadingState() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={28} className="animate-spin text-navy/30"/>
    </div>
  );
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-400">
      <AlertTriangle size={28} className="text-orange"/>
      <p className="text-sm">Failed to load dashboard.</p>
      {onRetry && <button onClick={onRetry} className="text-xs text-navy underline">Try again</button>}
    </div>
  );
}

// ── Responsive metrics grid ─────────────────────────────────────────────────
export function MetricsGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {children}
    </div>
  );
}

// ── ScrollTable ─────────────────────────────────────────────────────────────
export function ScrollTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[600px]">
        {children}
      </table>
    </div>
  );
}

export function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th className={clsx(
      'px-4 py-2.5 text-2xs font-semibold uppercase tracking-wide text-gray-400 bg-gray-50',
      right ? 'text-right' : 'text-left',
    )}>
      {children}
    </th>
  );
}

export function Td({ children, right, muted }: { children: React.ReactNode; right?: boolean; muted?: boolean }) {
  return (
    <td className={clsx(
      'px-4 py-3 border-t border-gray-50',
      right ? 'text-right' : '',
      muted ? 'text-gray-400' : 'text-gray-700',
    )}>
      {children}
    </td>
  );
}
